import * as THREE from "three";
import { viewportSize } from '../utils/ui.js';
import { TimeManager } from "../sky/TimeManager.js";
import { EnvironmentManager } from "./EnvironmentManager.js";
import { RoomBuilder } from "./RoomBuilder.js";
import { RoomBox } from "./RoomBox.js";
import { SHOW_DEV_TOOLS, IS_MOBILE, IS_APPLE } from "../config.js";
import { detectDeviceTier } from "../platform/deviceTier.js";
import { qualityProfile } from "../utils/deviceTier.js";
import { ensureAssets } from "./AssetSource.js";
import { migrateShadowQuality } from "../utils/constants.js";
import { skyMethods } from "./scene/skyMethods.js";
import { iblMethods } from "./scene/iblMethods.js";
import { roomMaterialMethods } from "./scene/roomMaterialMethods.js";
import { installMethods } from "../utils/installMethods.js";
import { setSplashProgress } from "../ui/splash.js";

/**
 * SceneManager - Central manager for the 3D scene
 *
 * Responsibilities:
 * - Scene and renderer creation/management
 * - Sky system (skybox, clouds, stars, cityscape)
 * - Lighting (hemisphere, ambient)
 * - Environment mapping
 * - Time management
 * - Coordinates EnvironmentManager and RoomBuilder
 */
export class SceneManager {
  constructor() {
    this.scene = null;
    this.renderer = null;
    this.loadingManager = new THREE.LoadingManager();
    this.isLoading = true;

    // Sub-managers
    this.environmentManager = new EnvironmentManager(this);
    this.roomBuilder = new RoomBuilder(this);
    this.roomBox = new RoomBox(this);

    // Sky system
    this.skybox = null;
    this.clouds = null;
    this.stars = null;
    this.cityscape = null;
    this.timeManager = null;
    this.timeOfDay = "Midday";

    // Lighting
    this.hemisphereLight = null;
    // Pixel-ratio cap, reduced targets and default shadows; re-resolved for the device tier in init().
    this._setQuality(null);
    // Shadow maps are static most of the time (furniture/room rarely move), so
    // every shadow-casting light has autoUpdate OFF and the contact-shadow blob is
    // only redrawn on demand. requestShadowUpdate() sets this small frame budget;
    // render() spends it. >0 means "re-render shadows this frame".
    this._shadowRefreshFrames = 0;
    // Multiplier on hemisphere/ambient fill light. <1 darkens cast shadows by
    // letting less fill into shadowed areas (1.0 = original brightness).
    this.fillLightFactor = 0.6;

    // Environment map for realistic materials
    this.pmremGenerator = null;
    this.envMap = null;
    this.lastEnvUpdateTime = null;
    // "sky" = PMREM baked from the live sky, per furniture material (production path);
    // "gradient" / "room" = baked IBL on scene.environment, still dev-only evaluation.
    this.iblMode = SHOW_DEV_TOOLS ? "gradient" : "sky";
    // 0 so the dev popover starts silent; shipping a bake means a non-zero default here.
    this.environmentIntensity = 0;
    // Nadir/zenith luminance + tint and the curve between them, for the gradient bake.
    this.iblGradient = {
      down: 1.6, up: 0.2, exponent: 1.5,
      downColor: 0xffffff, upColor: 0xffffff,
    };
    if (SHOW_DEV_TOOLS) this.loadEnvironmentSettings();

    // Top-level app reference (set via setApp after managers are ready)
    this.app = null;

    // State management
    this.stateManager = null;

    // Texture loader (shared)
    this.textureLoader = null;
  }

  _setQuality(tier) {
    this.quality = qualityProfile({ tier, isMobile: IS_MOBILE, isApple: IS_APPLE });
    this.shadowQuality = this.quality.shadowQuality;
  }

  applyPixelRatio() {
    const ratio = Math.min(window.devicePixelRatio, this.quality.pixelRatio);
    if (this.renderer.getPixelRatio() !== ratio) this.renderer.setPixelRatio(ratio);
  }

  async init() {
    this.setupLoadingManager();
    this.createScene();
    this.createRenderer();
    const tierReady = detectDeviceTier(this.renderer.getContext());
    if (this.iblMode !== "sky") this.bakeIbl();
    this.createLighting();
    await this.initializeMaterials();
    this._setQuality(await tierReady);
    this.applyPixelRatio();
    // Room materials build synchronously all the way down from createRoom, so the default
    // environment's textures have to be on disk before this line rather than streaming on
    // demand the way models and fabrics do.
    await this.ensureEnvironmentAssets(
      this.environmentManager.environmentSettings?.defaultEnvironment,
    );
    this.roomBuilder.createRoom();
    // loadEnvironmentData() only records the default; changeEnvironment applies its sky, background,
    // lighting mode and surface visibility. Without this the scene boots as the raw blue sky.
    this.environmentManager.changeEnvironment(this.environmentManager.environmentSettings.defaultEnvironment);

    // Initialize time manager
    this.timeManager = new TimeManager(12, 0);
  }

  // ===== LOADING MANAGER =====

  setupLoadingManager() {
    this.loadingManager.onLoad = () => {};

    this.loadingManager.onProgress = (url, loaded, total) => {
      const progress = (loaded / total) * 100;
      this.updateLoadingProgress(progress);
    };

    this.loadingManager.onError = (url) => {
      console.error("Error loading asset:", url);
    };
  }

  updateLoadingProgress(progress) {
    const progressBar = document.getElementById("loading-progress");
    if (progressBar) {
      progressBar.classList.add("loading-progress-bar");
      progressBar.style.width = `${progress}%`;
    }
    setSplashProgress(progress);
  }

  async hideLoadingScreen() {
    await new Promise(resolve => setTimeout(resolve, 250));

    const loadingScreen = document.getElementById("loading-screen");
    if (loadingScreen) {
      loadingScreen.classList.remove("loading-screen-visible");
      loadingScreen.classList.add("loading-screen-hidden");
      setTimeout(() => {
        loadingScreen.classList.add("display-none");
      }, 300);
    }
    this.isLoading = false;
  }

  // ===== SCENE CREATION =====

  createScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x87ceeb);
    this.createDynamicSky();
  }

  createRenderer() {
    const container = document.getElementById("canvas-container");

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
      // The window glass masks the sky through the stencil (src/sky/skyMask.js).
      stencil: true,
    });
    const { width: vw, height: vh } = viewportSize();
    this.renderer.setSize(vw, vh);

    this.applyPixelRatio();

    this.renderer.shadowMap.enabled = true;
    // VSM (Variance Shadow Maps) pre-blurs the depth map for smooth, area-light
    // penumbras. Softness is driven by shadow.radius + shadow.blurSamples (the
    // "samples" control in three's webgl_shadowmap_vsm example).
    this.renderer.shadowMap.type = THREE.VSMShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    this._setupContextLossRecovery(this.renderer.domElement);

    container.appendChild(this.renderer.domElement);

    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.pmremGenerator.compileEquirectangularShader();
  }

  /**
   * A WebGL context can be dropped by the OS under memory pressure — routine when the app
   * is backgrounded on mobile. Without a handler the canvas just goes black forever with no
   * error. Persist the layout first, then reload: rebuilding every texture, BVH and render
   * target in place is not worth the code when the design round-trips through the save path.
   */
  _setupContextLossRecovery(canvas) {
    canvas.addEventListener("webglcontextlost", (event) => {
      // Without preventDefault the context is never eligible for restoration.
      event.preventDefault();
      console.warn("[renderer] WebGL context lost");
      this.app?.getStateManager()?.saveSceneState();
      this.app?.pause?.();
    });

    canvas.addEventListener("webglcontextrestored", () => {
      console.warn("[renderer] WebGL context restored — reloading");
      window.location.reload();
    });
  }

  // ===== LIGHTING =====

  createLighting() {
    this.hemisphereLight = new THREE.HemisphereLight(0x87ceeb, 0x362d1a, 0.6 * this.fillLightFactor);
    this.scene.add(this.hemisphereLight);
  }

  /** Set the ceiling light's intensity (from environments.json lighting.ceiling.intensity). */
  setCeilingLightIntensity(intensity) {
    if (!Number.isFinite(intensity)) return;
    const ceilingLight = this.roomBuilder?.getCeilingLight();
    if (ceilingLight) ceilingLight.intensity = intensity;
  }

  setShadowQuality(quality) {
    const ceilingLight = this.roomBuilder.getCeilingLight();
    const contactShadow = this.roomBuilder.getContactShadow();
    if (!ceilingLight || !contactShadow) return;

    quality = migrateShadowQuality(quality);
    this.shadowQuality = quality;

    if (quality === "contact") {
      ceilingLight.castShadow = false;
      // The cheap top-down contact-shadow blob IS the ground shadow in this mode,
      // so the sun must NOT also cast a real (per-frame) shadow map. Leaving it on
      // (the previous behaviour) meant contact mode paid for BOTH shadow systems
      // every frame — a major cause of the GPU running hot. The sun still lights
      // the scene; it just doesn't cast.
      if (this.skybox?.sun) this.skybox.sun.castShadow = false;
      contactShadow.getMesh().visible = true;
      // A rebuild hands back a fresh blob at construction opacity, and this runs after one.
      contactShadow.setEnabled(true);

      if (this.skybox?.sun?.intensity === 0) {
        this.skybox.sun.intensity = 0.9;
      }
      if (this.roomBuilder.floor) {
        this.roomBuilder.floor.receiveShadow = true;
      }
    } else if (quality === "vsm") {
      this._configureVSMShadows(ceilingLight, this.quality.reduced);
      contactShadow.getMesh().visible = false;
      if (this.roomBuilder.floor) {
        this.roomBuilder.floor.receiveShadow = true;
      }
    } else if (quality === "none") {
      ceilingLight.castShadow = false;
      if (this.skybox?.sun) {
        this.skybox.sun.castShadow = false;
        this.skybox.sun.intensity = 0;
      }
      contactShadow.getMesh().visible = false;
    }

    // Keep a studio rig (white room) in sync — its key light casts real shadows
    // only in "vsm" quality (contact/none fall back to the blob / nothing).
    this.environmentManager?.refreshStudioLighting?.();

    // Shadow casters / quality just changed — render the new shadow map(s) and
    // contact blob on the next frame (shadows don't auto-update anymore).
    this.requestShadowUpdate();
  }

  // Shared VSM tuning (map size, blur, bias) for any shadow-casting light, plus
  // a map dispose so the new format/size takes effect on the next render.
  // blurSamples is the VSM gaussian tap count ("samples" in the vsm example);
  // VSM wants bias = 0 and leans on normalBias — negative bias reintroduces acne.
  _applyVSMShadowParams(shadow, reduced) {
    shadow.mapSize.set(2048, 2048);
    shadow.radius = reduced ? 3 : 4;
    shadow.blurSamples = reduced ? 8 : 16;
    shadow.bias = 0;
    shadow.normalBias = 0.03;
    // Static scene: don't re-render this shadow map every frame. It's flagged
    // needsUpdate on demand by render() when _shadowRefreshFrames is spent.
    shadow.autoUpdate = false;
    if (shadow.map) {
      shadow.map.dispose();
      shadow.map = null;
    }
  }

  _configureVSMShadows(ceilingLight, reduced) {
    ceilingLight.castShadow = true;
    // The ceiling light is a wide SpotLight (~163° shadow FOV). focus < 1 shrinks
    // the *shadow* camera (not the light cone) so texels concentrate on the
    // furniture; 0.8 still covers the whole 10x10 floor (~7.3m radius). Tight
    // near/far maximizes VSM depth precision so small/thin parts keep their
    // shadows instead of bleeding away. (SpotLightShadow ignores camera.fov.)
    ceilingLight.shadow.focus = 0.8;
    ceilingLight.shadow.camera.near = 0.8;
    ceilingLight.shadow.camera.far = 10;
    this._applyVSMShadowParams(ceilingLight.shadow, reduced);

    if (this.skybox?.sun) {
      this.skybox.sun.castShadow = true;
      if (this.skybox.sun.intensity === 0) {
        this.skybox.sun.intensity = 0.9;
      }
      this._applyVSMShadowParams(this.skybox.sun.shadow, reduced);
    }
  }

  // ===== MATERIALS =====

  async initializeMaterials() {
    this.textureLoader = new THREE.TextureLoader(this.loadingManager);
    await this.environmentManager.loadEnvironmentData();
  }

  // ===== UPDATE & RENDER =====

  update(deltaTime, camera) {
    // Embed forces a white room (studio lights, sky hidden) and never animates time,
    // so the skybox/clouds/stars are invisible — skip their per-frame work entirely.
    // Shadows are unaffected: they're baked once with autoUpdate off.
    if (this.app?.isEmbedMode) return;

    this._updateSky(deltaTime, camera);
  }

  /**
   * Request that shadows be re-rendered over the next few frames. Light shadow
   * maps have autoUpdate disabled and the contact-shadow blob is otherwise never
   * redrawn, so any code that moves furniture, changes the room/environment, or
   * toggles a shadow caster MUST call this — otherwise shadows go stale. The small
   * multi-frame budget covers cases where the triggering transform settles a frame
   * later (e.g. a variant/leg tween or a snap re-bind).
   */
  requestShadowUpdate(frames = 2) {
    this._shadowRefreshFrames = Math.max(this._shadowRefreshFrames, frames);
  }

  render(camera) {
    const contactShadow = this.roomBuilder.getContactShadow();
    const ceilingLight = this.roomBuilder.getCeilingLight();

    // Shadows only re-render when something requested it (see requestShadowUpdate).
    // The rest of the time the previously-rendered maps / contact blob are reused,
    // which keeps the GPU idle on a static scene.
    if (this._shadowRefreshFrames > 0) {
      // autoUpdate is off on every shadow-casting light, so flag the active ones
      // for a single re-render this frame.
      if (this.skybox?.sun?.castShadow) this.skybox.sun.shadow.needsUpdate = true;
      if (ceilingLight?.castShadow) ceilingLight.shadow.needsUpdate = true;
      this.environmentManager?.markStudioShadowsNeedUpdate?.();

      // Contact quality renders the soft contact-shadow blob (in every room,
      // including studio rooms — the studio rig only casts real shadows in VSM
      // quality, so there's nothing to double up with here).
      if (this.shadowQuality === "contact" && contactShadow) {
        contactShadow.render();
      }

      this._shadowRefreshFrames--;
    }

    this.renderer.render(this.scene, camera);
  }

  // ===== DELEGATED METHODS (for backwards compatibility) =====

  // Environment methods
  changeEnvironment(environmentKey) {
    const result = this.environmentManager.changeEnvironment(environmentKey);
    // Walls/floor/lighting may have changed (e.g. sky room ↔ white studio) —
    // re-render the otherwise-static shadows.
    this.requestShadowUpdate();
    return result;
  }

  getEnvironments() {
    return this.environmentManager.getEnvironments();
  }

  getCurrentEnvironment() {
    return this.environmentManager.getCurrentEnvironment();
  }

  // Room methods
  changeRoomSize(width, depth, height = null) {
    const result = this.roomBuilder.changeRoomSize(width, depth, height);
    if (result) {
      this.stateManager?.saveSceneState();
      // Room geometry was rebuilt — refresh shadows (no longer auto-updated).
      this.requestShadowUpdate();
    }
    return result;
  }

  getDoorManager() {
    return this.roomBuilder.getDoorManager();
  }

  // ===== PUBLIC API =====

  getScene() {
    return this.scene;
  }

  getRenderer() {
    return this.renderer;
  }

  getLoadingManager() {
    return this.loadingManager;
  }

  getEnvironmentManager() {
    return this.environmentManager;
  }

  getRoomBuilder() {
    return this.roomBuilder;
  }

  getRoomBox() {
    return this.roomBox;
  }

  getSun() {
    return this.skybox ? this.skybox.sun : null;
  }

  getTimeManager() {
    return this.timeManager;
  }

  // ===== STATE MANAGEMENT =====

  getEnvironmentState() {
    // Mid-trace the rig may have swapped in a product-shot room; persist the user's, not the shot's.
    const shot = this.app?.getPathTracingManager?.()?.sceneOverrides?.getOriginal?.() || null;
    const state = {
      ...this.environmentManager.getEnvironmentState(),
      roomSize: { ...(shot?.roomSize || this.roomBuilder.getRoomSize()) },
      timeOfDay: this.timeOfDay,
      whiteFloor: shot ? shot.whiteFloor : this.roomBuilder.whiteFloorEnabled
    };

    if (this.timeManager) {
      state.timeHours = this.timeManager.getHours();
      state.timeMinutes = this.timeManager.getMinutes();
    }

    return state;
  }

  setEnvironmentState(state) {
    try {
      const originalStateManager = this.stateManager;
      this.stateManager = null;

      this.environmentManager.setEnvironmentState(state);

      if (state.roomSize) {
        this.roomBuilder.setRoomSize(state.roomSize);
        this.roomBuilder.createRoom();
      }

      if (this.timeManager && state.timeHours !== undefined && state.timeMinutes !== undefined) {
        this.timeManager.setTime(state.timeHours, state.timeMinutes);
      }

      // Last: the floor only exists once changeEnvironment / createRoom above have rebuilt it.
      // Unconditional, so a preset saved without the flag clears a leftover override.
      this.roomBuilder.setWhiteFloor(!!state.whiteFloor);

      this.stateManager = originalStateManager;
      return true;
    } catch (error) {
      console.error('Failed to set environment state:', error);
      return false;
    }
  }

  setApp(app) {
    this.app = app;
  }

  setStateManager(stateManager) {
    this.stateManager = stateManager;
    this.environmentManager.setStateManager(stateManager);
  }

  initializeDynamicLighting() {
    this.lastEnvUpdateTime = this.timeOfDay;
    this.updateAmbientLighting(this.timeOfDay);

    const ceilingLight = this.roomBuilder.getCeilingLight();
    const contactShadow = this.roomBuilder.getContactShadow();

    if (ceilingLight && contactShadow) {
      this.setShadowQuality(this.shadowQuality);
    }

    // Render the initial shadow maps / contact blob now that lighting is configured
    // (autoUpdate is off, so without this the opening frames would show no shadows).
    this.requestShadowUpdate();

    setTimeout(() => {
      this.updateEnvironmentMap();
    }, 500);
  }

  /**
   * Stream the floor/wall textures an environment needs. No-op on web.
   * Failures are warned, not thrown: a room with untextured surfaces still beats a
   * scene that refuses to boot.
   */
  async ensureEnvironmentAssets(envKey) {
    const paths = this.environmentManager?.environmentAssetPaths?.(envKey) ?? [];
    if (!paths.length) return;
    try {
      await ensureAssets(paths);
    } catch (error) {
      console.warn("[scene] environment textures unavailable:", error);
    }
  }

}

installMethods(SceneManager.prototype, skyMethods, iblMethods, roomMaterialMethods);
